import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Sparkles, UserCheck, ShieldCheck, Camera, BookOpen, Calendar, MessageCircle, ArrowRight } from "lucide-react";

/**
 * PractitionerWelcome — first-time landing for newly-subscribed Providers.
 *
 * Shown immediately after Stripe confirms their $49.99/mo subscription.
 * Guides them through the 5-step "get your first paying client" path so
 * they don't churn on day 1 staring at an empty profile.
 */
export default function PractitionerWelcome() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-10" data-testid="practitioner-welcome-page">
        {/* Hero */}
        <div className="text-center space-y-4">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-secondary/40 bg-secondary/[0.08]"
            style={{ color: "#D4B58C" }}
            data-testid="practitioner-welcome-chip"
          >
            <Sparkles size={12} strokeWidth={1.75} />
            <span className="label-mystic">Subscription Active</span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl" data-testid="practitioner-welcome-heading">
            Welcome to the Coven
          </h1>
          <p className="text-foreground/75 max-w-xl mx-auto text-base leading-relaxed">
            Your subscription is live. Here are the five things to do this week
            to land your first paying seeker — each step compounds your trust
            score and unlocks higher-tier badges.
          </p>
        </div>

        {/* 5-step playbook */}
        <div className="space-y-3" data-testid="practitioner-welcome-playbook">
          <StepCard n={1} to="/profile/me" icon={<UserCheck size={22} />} kicker="Step 1 · Identity" title="Complete ID verification" body="Upload a government ID + selfie. Approved in under 60 seconds, gold-locks your Verified Practitioner badge across the Cauldron." testid="practitioner-step-kyc" />
          <StepCard n={2} to="/profile/me" icon={<Camera size={22} />} kicker="Step 2 · Story" title="Add a profile photo + 200-word bio" body="Profiles with photos book 4.2× more sessions. Speak directly to the seeker you most love working with." testid="practitioner-step-bio" />
          <StepCard n={3} to="/profile/me" icon={<BookOpen size={22} />} kicker="Step 3 · Offering" title="Publish your first service" body="One 30-minute reading at any price you choose. You keep 90% — Cauldron takes a 10% platform fee, escrow-protected." testid="practitioner-step-service" />
          <StepCard n={4} to="/profile/me" icon={<Calendar size={22} />} kicker="Step 4 · Availability" title="Set your booking calendar" body="Pick the hours you're willing to read. Seekers can only book inside your declared windows — no surprise pings at 3 AM." testid="practitioner-step-calendar" />
          <StepCard n={5} to="/messages" icon={<MessageCircle size={22} />} kicker="Step 5 · Reach" title="Reply within 8 hours" body="Practitioners who answer their first inquiry inside 8 hours book 3× more often. Notifications fire to your email + Cauldron inbox." testid="practitioner-step-messages" />
        </div>

        {/* Trust + payout note */}
        <div
          className="welcome-note relative overflow-hidden parchment p-6 flex items-start gap-4"
          data-testid="practitioner-welcome-note"
        >
          <ShieldCheck size={22} className="mt-0.5 shrink-0 relative z-10" style={{ color: "#D4B58C" }} strokeWidth={1.75} />
          <div className="text-base leading-relaxed text-[#F4EEDA] relative z-10">
            <div className="font-serif text-lg mb-2" style={{ color: "#D4B58C" }}>
              A note from the Cauldron
            </div>
            We onboard seekers slowly and deliberately so the trust signal of
            your Verified badge holds value. New paying clients arrive every
            day — please be patient as the platform grows. Every five-star
            review you earn climbs you toward Temple and Elder tier, which
            unlocks platform-wide promotion. <span style={{ color: "#D4B58C" }} className="font-medium">Quality over scale, always.</span>
          </div>
        </div>

        {/* Continue */}
        <div className="text-center pt-4 flex flex-col items-center gap-3">
          <Link
            to="/profile/me"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85"
            data-testid="practitioner-welcome-cta-profile"
          >
            Go to my profile <ArrowRight size={11} />
          </Link>
          <Link
            to="/feed"
            className="inline-flex items-center gap-2 text-secondary hover:text-primary text-sm font-semibold uppercase tracking-[0.18em] underline underline-offset-4 decoration-secondary/60 hover:decoration-primary"
            data-testid="practitioner-welcome-skip"
          >
            Skip — take me to the Feed <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function StepCard({ n, to, icon, kicker, title, body, testid }) {
  return (
    <Link
      to={to}
      className="parchment p-5 flex items-start gap-4 group transition-colors hover:border-secondary/60"
      data-testid={testid}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-full border border-secondary/40 flex items-center justify-center font-serif text-base"
        style={{ color: "#D4B58C" }}
      >
        {n}
      </div>
      <div className="text-secondary mt-1 shrink-0" style={{ color: "#D4B58C" }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-mystic text-secondary/85 mb-1 text-[10px]">{kicker}</div>
        <h3 className="font-serif text-lg group-hover:text-secondary transition-colors">{title}</h3>
        <p className="text-foreground/70 text-sm leading-relaxed mt-1">{body}</p>
      </div>
      <ArrowRight size={14} className="text-foreground/40 group-hover:text-secondary mt-3 shrink-0" />
    </Link>
  );
}
